import { Button, Input, PageHeader, Skeleton, useToast } from '@zyxantech/tux'
import { ArrowLeft, ArrowRight, Check, Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useCreateReportMutation,
  useGetReportByIdQuery,
  useUpdateReportMutation,
} from '@/api/reports.api'
import { useGetTagsQuery } from '@/api/tags.api'
import { ErrorState } from '@/components/shared/ErrorState'
import { PAGINATION } from '@/constants/pagination'
import { ROUTES } from '@/constants/routes'

type Format = 'pdf' | 'csv' | 'xlsx'

const FORMAT_OPTIONS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'csv', label: 'CSV' },
  { value: 'xlsx', label: 'Excel (XLSX)' },
]

const STEP_LABELS = ['Details', 'Format', 'Tags', 'Review']

export function ReportBuilderPage() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const isEditing = Boolean(id)

  const { data: existing, isLoading: loadingExisting, error: loadError } = useGetReportByIdQuery(
    id ?? '',
    { skip: !id },
  )

  const [step, setStep] = useState(1)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [format, setFormat] = useState<Format>('csv')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [schedule, setSchedule] = useState('')
  const [tagSearch, setTagSearch] = useState('')
  const [hydrated, setHydrated] = useState(false)

  const [createReport, { isLoading: isCreating }] = useCreateReportMutation()
  const [updateReport, { isLoading: isUpdating }] = useUpdateReportMutation()
  const isSaving = isCreating || isUpdating

  const { data: tagsData } = useGetTagsQuery({
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
  })

  // Hydrate from existing report exactly once when editing.
  useEffect(() => {
    if (!existing || hydrated) return
    setName(existing.name)
    setDescription(existing.description ?? '')
    const config = existing.config as {
      format?: Format
      tagIds?: string[]
      schedule?: string
    }
    if (config.format) setFormat(config.format)
    setSelectedTagIds(config.tagIds ?? [])
    setSchedule(existing.schedule ?? '')
    setHydrated(true)
  }, [existing, hydrated])

  const filteredTags = useMemo(() => {
    const q = tagSearch.trim().toLowerCase()
    return (tagsData?.items ?? []).filter(
      (t) => !q || t.name.toLowerCase().includes(q) || t.address.toLowerCase().includes(q),
    )
  }, [tagsData, tagSearch])

  const tagMap = useMemo(() => {
    const m = new Map<string, string>()
    for (const t of tagsData?.items ?? []) m.set(t.id, t.name)
    return m
  }, [tagsData])

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
    )
  }

  const canAdvance = (): boolean => {
    if (step === 1) return name.trim().length > 0
    if (step === 3) return selectedTagIds.length > 0
    return true
  }

  const handleSave = async () => {
    const body = {
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      config: { format, tagIds: selectedTagIds },
      ...(schedule.trim() ? { schedule: schedule.trim() } : {}),
    }
    try {
      if (isEditing && id) {
        await updateReport({ id, body }).unwrap()
        toast.show('Report updated', 'success')
      } else {
        await createReport(body).unwrap()
        toast.show('Report created', 'success')
      }
      navigate(ROUTES.REPORTS)
    } catch {
      toast.show('Failed to save report', 'error')
    }
  }

  if (isEditing && loadingExisting) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton height={28} width="30%" />
        <Skeleton height={400} />
      </div>
    )
  }
  if (isEditing && loadError) {
    return (
      <div className="p-6">
        <ErrorState error={loadError} />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <button
        type="button"
        onClick={() => navigate(ROUTES.REPORTS)}
        className="mb-4 flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary"
      >
        <ArrowLeft size={14} /> Reports
      </button>

      <PageHeader
        title={isEditing ? 'Edit report' : 'New report'}
        subtitle={`Step ${step} of ${STEP_LABELS.length} — ${STEP_LABELS[step - 1]}`}
      />

      {/* Step indicator */}
      <div className="mb-8 flex gap-2">
        {STEP_LABELS.map((label, i) => {
          const num = i + 1
          const done = num < step
          const active = num === step
          return (
            <div key={label} className="flex flex-1 flex-col items-center gap-1">
              <div
                className={[
                  'flex size-7 items-center justify-center rounded-full text-xs font-semibold',
                  active ? 'bg-accent text-bg-base' : '',
                  done ? 'bg-success text-bg-base' : '',
                  !active && !done ? 'bg-bg-surface text-text-muted' : '',
                ].join(' ')}
              >
                {done ? <Check size={13} /> : num}
              </div>
              <span
                className={[
                  'text-[10px] font-medium',
                  active ? 'text-accent' : 'text-text-muted',
                ].join(' ')}
              >
                {label}
              </span>
            </div>
          )
        })}
      </div>

      {/* Step content */}
      <div className="rounded-lg border border-border-subtle bg-bg-surface p-6">
        {step === 1 && (
          <Step1Details
            name={name}
            description={description}
            onName={setName}
            onDescription={setDescription}
          />
        )}
        {step === 2 && <Step2Format format={format} onFormat={setFormat} />}
        {step === 3 && (
          <Step3Tags
            search={tagSearch}
            onSearch={setTagSearch}
            tags={filteredTags}
            selected={selectedTagIds}
            onToggle={toggleTag}
          />
        )}
        {step === 4 && (
          <Step4Review
            name={name}
            description={description}
            format={format}
            selectedTagIds={selectedTagIds}
            tagMap={tagMap}
            schedule={schedule}
            onSchedule={setSchedule}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="mt-4 flex justify-between">
        <Button
          variant="outline"
          icon={<ArrowLeft size={14} />}
          onClick={() => (step > 1 ? setStep((s) => s - 1) : navigate(ROUTES.REPORTS))}
        >
          {step === 1 ? 'Cancel' : 'Back'}
        </Button>

        {step < STEP_LABELS.length ? (
          <Button
            variant="primary"
            disabled={!canAdvance()}
            onClick={() => setStep((s) => s + 1)}
          >
            Next <ArrowRight size={14} className="ml-1" />
          </Button>
        ) : (
          <Button
            variant="primary"
            disabled={!canAdvance() || isSaving}
            loading={isSaving}
            onClick={() => void handleSave()}
          >
            {isEditing ? 'Save changes' : 'Create report'}
          </Button>
        )}
      </div>
    </div>
  )
}

function Step1Details({
  name,
  description,
  onName,
  onDescription,
}: {
  name: string
  description: string
  onName: (v: string) => void
  onDescription: (v: string) => void
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-text-primary">
          Name <span className="text-danger">*</span>
        </label>
        <Input
          value={name}
          onChange={(e) => onName(e.target.value)}
          placeholder="Monthly temperature report"
          autoFocus
        />
      </div>
      <div>
        <label className="mb-1.5 block text-sm font-medium text-text-primary">
          Description
        </label>
        <Input
          value={description}
          onChange={(e) => onDescription(e.target.value)}
          placeholder="Optional description"
        />
      </div>
    </div>
  )
}

function Step2Format({ format, onFormat }: { format: Format; onFormat: (f: Format) => void }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">Choose the export file format.</p>
      <div className="space-y-2">
        {FORMAT_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            className={[
              'flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 transition-colors',
              format === opt.value
                ? 'border-accent bg-accent/5 text-text-primary'
                : 'border-border-subtle bg-bg-base text-text-secondary hover:border-accent/50',
            ].join(' ')}
          >
            <input
              type="radio"
              name="format"
              value={opt.value}
              checked={format === opt.value}
              onChange={() => onFormat(opt.value as Format)}
              className="accent-accent"
            />
            <span className="font-medium">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

function Step3Tags({
  search,
  onSearch,
  tags,
  selected,
  onToggle,
}: {
  search: string
  onSearch: (v: string) => void
  tags: { id: string; name: string; address: string; unit: string | null }[]
  selected: string[]
  onToggle: (id: string) => void
}) {
  const selectedSet = useMemo(() => new Set(selected), [selected])

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">
        Select tags to include in the report.{' '}
        <span className="font-medium text-text-primary">{selected.length} selected</span>
      </p>
      <Input
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search tags…"
        leadingIcon={<Search size={14} />}
      />
      <div className="max-h-[320px] overflow-auto rounded-md border border-border-subtle">
        {tags.length === 0 ? (
          <p className="py-6 text-center text-sm text-text-muted">No tags found.</p>
        ) : (
          tags.map((tag) => {
            const checked = selectedSet.has(tag.id)
            return (
              <label
                key={tag.id}
                className={[
                  'flex cursor-pointer items-center gap-3 border-b border-border-subtle px-4 py-2.5 last:border-b-0 transition-colors',
                  checked ? 'bg-accent/5' : 'hover:bg-bg-surface',
                ].join(' ')}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(tag.id)}
                  className="accent-accent"
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary">{tag.name}</p>
                  <p className="font-mono text-xs text-text-muted">{tag.address}</p>
                </div>
                {tag.unit && (
                  <span className="ml-auto shrink-0 text-xs text-text-muted">{tag.unit}</span>
                )}
              </label>
            )
          })
        )}
      </div>
    </div>
  )
}

function Step4Review({
  name,
  description,
  format,
  selectedTagIds,
  tagMap,
  schedule,
  onSchedule,
}: {
  name: string
  description: string
  format: Format
  selectedTagIds: string[]
  tagMap: Map<string, string>
  schedule: string
  onSchedule: (v: string) => void
}) {
  return (
    <div className="space-y-5">
      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
          Summary
        </h3>
        <div className="space-y-1.5 text-sm">
          <Row label="Name" value={name} />
          {description && <Row label="Description" value={description} />}
          <Row label="Format" value={format.toUpperCase()} />
          <Row label="Tags" value={`${selectedTagIds.length} selected`} />
        </div>
      </section>

      {selectedTagIds.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
            Selected tags
          </h3>
          <ul className="space-y-1">
            {selectedTagIds.map((id) => (
              <li key={id} className="text-sm text-text-secondary">
                {tagMap.get(id) ?? id}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
          Schedule (optional)
        </h3>
        <Input
          value={schedule}
          onChange={(e) => onSchedule(e.target.value)}
          placeholder="Cron expression, e.g. 0 8 * * 1"
          className="font-mono"
        />
        <p className="mt-1 text-xs text-text-muted">
          Leave blank to generate on demand only.
        </p>
      </section>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="w-24 shrink-0 text-text-muted">{label}</span>
      <span className="text-text-primary">{value}</span>
    </div>
  )
}
